import NgoLayout from '../../../../layouts/Ngo/NgoLayout';
import '../../../../assets/css/Ngo/controlpanelCSS/donorrewardslevel.css';
// ============================================
// DonorRewards.js - Main Component File
// ============================================

import React, { useState } from 'react';
import { Search, Award, Gift, CheckCircle, Clock, Eye, Package, Medal, Star, Crown, X, Download } from 'lucide-react';

const DonorRewardsSystem = () => {
  const [searchName, setSearchName] = useState('');
  const [filterLevel, setFilterLevel] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [remarks, setRemarks] = useState('');

  // Sample Data
  const donors = [
    { id: 1, name: 'Ali Khan', level: 'Bronze', donation: 'PKR 25,000', reward: 'Badge', status: 'Pending', date: '2024-01-15' },
    { id: 2, name: 'Sara Malik', level: 'Silver', donation: 'PKR 75,000', reward: 'Certificate', status: 'Delivered', date: '2024-01-14' },
    { id: 3, name: 'Ahmed Raza', level: 'Gold', donation: 'PKR 150,000', reward: 'Cert + Badge', status: 'Pending', date: '2024-01-13' },
    { id: 4, name: 'Usman Ali', level: 'Platinum', donation: 'PKR 600,000', reward: 'Premium Pack', status: 'Delivered', date: '2024-01-12' },
    { id: 5, name: 'Fatima Ahmed', level: 'Bronze', donation: 'PKR 30,000', reward: 'Badge', status: 'Pending', date: '2024-01-11' },
    { id: 6, name: 'Hassan Tariq', level: 'Silver', donation: 'PKR 80,000', reward: 'Certificate', status: 'Delivered', date: '2024-01-10' },
  ];

  const levelStats = [
    { name: 'Bronze', count: 45, icon: Medal },
    { name: 'Silver', count: 28, icon: Award },
    { name: 'Gold', count: 15, icon: Star },
    { name: 'Platinum', count: 5, icon: Crown }
  ];

  // Filter Logic
  const filteredDonors = donors.filter(donor => {
    const matchesName = donor.name.toLowerCase().includes(searchName.toLowerCase());
    const matchesLevel = filterLevel === 'All' || donor.level === filterLevel;
    const matchesStatus = filterStatus === 'All' || donor.status === filterStatus;
    return matchesName && matchesLevel && matchesStatus;
  });

  // Calculate Counts
  const pendingCount = donors.filter(d => d.status === 'Pending').length;
  const deliveredCount = donors.filter(d => d.status === 'Delivered').length;

  // Event Handlers
  const handleMarkAsIssued = (donor) => {
    setSelectedDonor(donor);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    console.log('Confirmed for:', selectedDonor.name);
    console.log('Remarks:', remarks);
    setShowConfirmModal(false);
    setSelectedDonor(null);
    setRemarks('');
  };

  const handleReset = () => {
    setSearchName('');
    setFilterLevel('All');
    setFilterStatus('All');
  };

  const getLevelColor = (level) => {
    const colors = {
      Bronze: 'level-bronze',
      Silver: 'level-silver',
      Gold: 'level-gold',
      Platinum: 'level-platinum'
    };
    return colors[level] || 'level-bronze';
  };

  const getRequestTypeClass = (level) => {
    const classes = {
      Bronze: 'request-type-bronze',
      Silver: 'request-type-silver',
      Gold: 'request-type-gold',
      Platinum: 'request-type-platinum'
    };
    return classes[level] || 'request-type-bronze';
  };

  return (
    <NgoLayout>
    <div className="donor-rewards-container">
      <div className="donor-rewards-wrapper">
        
        {/* Header Section */}
        <div className="header-section">
          <div className="header-content">
            <div className="header-text">
              <h1 className="header-title">Donor Levels & Rewards</h1>
              <p className="header-subtitle">Track donor levels and manage reward issuance</p>
            </div>
          </div>
          <button className="export-btn">
            <Download size={20} />
            <span>Export Report</span>
          </button>
        </div>

        {/* Status Tabs */}
        <div className="status-tabs">
          <div className="status-tab status-tab-pending active">
            <Clock size={20} />
            <div className="status-tab-content">
              <span className="status-tab-label">Pending Verification</span>
              <span className="status-tab-count">{pendingCount}</span>
            </div>
          </div>
          <div className="status-tab status-tab-approved">
            <CheckCircle size={20} />
            <div className="status-tab-content">
              <span className="status-tab-label">Approved Requests</span>
              <span className="status-tab-count">{deliveredCount}</span>
            </div>
          </div>
          <div className="status-tab status-tab-rejected">
            <X size={20} />
            <div className="status-tab-content">
              <span className="status-tab-label">Rejected Requests</span>
              <span className="status-tab-count">1</span>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="search-section">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search by name, roll number, or request type"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Table Container */}
        <div className="table-container">
          <table className="donors-table">
            <thead>
              <tr>
                <th>Donor NAME</th>
                <th>REQUEST TYPE</th>
                <th>AMOUNT</th>
                <th>ASSIGNED REWARD</th>
                <th>STATUS</th>
                <th>SUBMITTED</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredDonors.map((donor) => (
                <tr key={donor.id}>
                  <td>
                    <div className="donor-name-cell">
                      <div className="donor-avatar">
                        {donor.name.charAt(0)}
                      </div>
                      <span className="donor-name">{donor.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`request-type-badge ${getRequestTypeClass(donor.level)}`}>
                      {donor.level === 'Bronze' && '📚 School Fee'}
                      {donor.level === 'Silver' && '📖 Books'}
                      {donor.level === 'Gold' && '👔 Uniform'}
                      {donor.level === 'Platinum' && '🎓 Premium'}
                    </span>
                  </td>
                  <td>
                    <span className="donation-amount">{donor.donation}</span>
                  </td>
                  <td>
                    <span className="reward-text">{donor.reward}</span>
                  </td>
                  <td>
                    {donor.status === 'Pending' ? (
                      <span className="status-badge status-badge-pending">
                        <Clock size={14} /> PENDING
                      </span>
                    ) : (
                      <span className="status-badge status-badge-delivered">
                        <CheckCircle size={14} /> DELIVERED
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="submitted-date">{donor.date}</span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleMarkAsIssued(donor)}
                      className="action-btn"
                    >
                      <Eye size={16} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && selectedDonor && (
          <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Confirm Reward Issuance</h3>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="modal-close"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <div className="modal-field-label">Donor Name</div>
                  <div className="modal-field-value">{selectedDonor.name}</div>
                </div>

                <div className="modal-field">
                  <div className="modal-field-label">Level</div>
                  <span className={`level-badge ${getLevelColor(selectedDonor.level)}`}>
                    {selectedDonor.level}
                  </span>
                </div>

                <div className="modal-field">
                  <div className="modal-field-label">Reward</div>
                  <div className="modal-field-value reward-value">
                    <Package size={20} />
                    {selectedDonor.reward}
                  </div>
                </div>

                <div className="modal-field">
                  <label className="modal-field-label">Remarks (optional)</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter any remarks..."
                    className="modal-textarea"
                    rows="3"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  onClick={handleConfirm}
                  className="modal-btn modal-btn-confirm"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="modal-btn modal-btn-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </NgoLayout>
  );
};

export default DonorRewardsSystem;